import { CheckCircleFilled } from '@ant-design/icons'
import React from 'react'
import { COLORS, eur, num } from './format'

const line = { display: 'flex', alignItems: 'baseline', gap: 8, padding: '7px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }
const label = { flex: 1, color: '#334155' }
const calc = { color: '#94a3b8', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }
const amount = { width: 96, textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }

const Row = ({ dot, children, units, price, value }) => (
  <div style={line}>
    {dot && <span style={{ width: 8, height: 8, borderRadius: 2, background: dot, flex: 'none', alignSelf: 'center' }} />}
    <span style={label}>{children}</span>
    {units != null && (
      <span style={calc}>
        {num(units)} × {eur(price, 3)}
      </span>
    )}
    <span style={amount}>{eur(value)}</span>
  </div>
)

const Total = ({ children, value, color }) => (
  <div style={{ ...line, borderBottom: 'none', borderTop: '1px solid #1e293b', fontWeight: 700, fontSize: 14 }}>
    <span style={label}>{children}</span>
    <span style={{ ...amount, color }}>{eur(value)}</span>
  </div>
)

const Note = ({ children }) => <p style={{ fontSize: 12, color: '#64748b', margin: '10px 0 0', lineHeight: 1.5 }}>{children}</p>

const box = { flex: '1 1 380px', minWidth: 0, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 18px' }
const title = { fontWeight: 700, fontSize: 15, marginBottom: 2 }
const subtitle = { fontSize: 12, color: '#64748b', marginBottom: 8 }

/**
 * Las dos caras de la facturación, con las cuentas del periodo: lo que cobramos al cliente
 * (por envío) y lo que nos cobra Orange (por lo que pasa después), y el margen que queda.
 */
const MoneyFlow = ({ totals: t, rates, invoice, invoiceOk }) => {
  const rcsPrice = t.rcsInitUnits ? t.retailRcs / t.rcsInitUnits : 0
  const smsPrice = t.smsChargedUnits ? (t.retailSms || 0) / t.smsChargedUnits : 0
  const cobrado = (t.retailRcs || 0) + (t.retailSms || 0)

  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div style={box}>
          <div style={title}>Lo que cobramos al cliente</div>
          <div style={subtitle}>Por cada envío de una campaña, a precio fijo</div>
          <Row units={t.rcsInitUnits} price={rcsPrice} value={t.retailRcs}>
            Mensaje inicial RCS
          </Row>
          <Row units={t.smsChargedUnits || 0} price={smsPrice} value={t.retailSms || 0}>
            SMS (por parte)
          </Row>
          <Total value={cobrado}>Total cobrado</Total>
          <Note>
            Se cobra cada envío de cada campaña: {num(t.rcsInitSubmitted)} envíos, un contacto que está en 3
            campañas se cobra 3 veces.
            {t.rcsInitSubmitted > t.rcsInitUnits
              ? ` ${num(t.rcsInitSubmitted - t.rcsInitUnits)} no recibieron el RCS y se cobran por su SMS de respaldo (una sola vez).`
              : ''}{' '}
            Lo que pasa después (si responde, cuántos mensajes le manda el bot) no cambia el precio.
          </Note>
        </div>

        <div style={box}>
          <div style={title}>Lo que nos cobra Orange (RCS)</div>
          <div style={subtitle}>Por lo que pasa con cada mensaje, no por contacto</div>
          <Row dot={COLORS.simple} units={t.simple} price={rates.simple} value={t.simple * rates.simple}>
            Mensaje sin respuesta en 24 h
          </Row>
          <Row dot={COLORS.conversacion} units={t.sessions} price={rates.conversacional} value={t.sessions * rates.conversacional}>
            Conversación (respondió)
          </Row>
          <Row dot={COLORS.basico} units={t.basico} price={rates.basico} value={t.basico * rates.basico}>
            Respuesta automática «flujo cerrado»
          </Row>
          <Total value={t.cost} color="#b45309">
            Total coste Orange
            {invoice && (
              <span style={{ fontWeight: 400, fontSize: 12, color: invoiceOk ? '#15803d' : '#b45309', marginLeft: 8 }}>
                {invoiceOk && <CheckCircleFilled />} factura {eur(invoice.total)}
              </span>
            )}
          </Total>
          <Note>
            Dentro de una conversación, todo lo que envía el bot en 24 h va incluido ({num(t.bundled)} mensajes
            gratis). Lo que escribe la persona nunca se cobra. Orange cobra también los mensajes que no llegan.
          </Note>
        </div>
      </div>

      <div
        style={{
          marginTop: 12,
          padding: '10px 18px',
          background: t.margin >= 0 ? '#f0fdf4' : '#fef2f2',
          borderRadius: 10,
          fontSize: 14,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8,
          alignItems: 'baseline'
        }}
      >
        <b>Margen RCS</b>
        <span style={{ color: '#475569' }}>
          = {eur(t.retailRcs)} cobrado por RCS − {eur(t.cost)} de Orange =
        </span>
        <b style={{ color: t.margin >= 0 ? '#15803d' : '#b91c1c', fontSize: 16 }}>{eur(t.margin)}</b>
        <span style={{ color: '#64748b', fontSize: 12 }}>
          · El SMS ({eur(t.retailSms || 0)}) no entra: lo cobra otro proveedor y su coste no está aquí.
        </span>
      </div>
    </div>
  )
}

export default MoneyFlow
